/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/useQaItpExport.ts
 * FILE_ID: JOBHUB-QA-ITP-EXPORT-HOOK-001
 * PURPOSE: Edit, print, download, sign and send one ITP from the shared HTML builder.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { snapshotQaPhotoViews, useIsMdUp } from '@quotech/ui'
import { buildItpRequestHtml, printItpHtml } from '../../../qa/itpDocument'
import { fillLibraryPhotoSrc } from '../../../qa/fillLibraryPhotoSrc'
import type { ItpCoverFields } from '../../../qa/itpCover'
import { defaultItpAttachmentName } from '../../../qa/itpReportTitle'
import { pickPdfSaveLocation, writePdfToHandleOrDownload } from '../../../qa/downloadBlobFile'
import { needsPrintPopup, printPdfBytes } from '../../../qa/printPdfBytes'
import { saveItpPdf } from '../../../qa/saveItpPdf'
import type { QaApi, QaAreaExecution } from '../../../qa/qaApi'
import { expandItpItemIds, type QaItpRequest } from '../qaItpScope'
import { areasWithEvidenceUrls } from '../useQaEvidenceUrls'
import { useJobHubDataAdapter } from '../../../jobHubDataAdapter'
import { patchItpCoverImage, patchItpCoverTitle } from '../../../qa/patchItpCover'
import { useQaItpExportCover } from './useQaItpExportCover'
import { useQaItpPreviewRemove } from './useQaItpPreviewRemove'
import { flushPendingQuoteDetails } from '../useJobQuoteDetails'
import { useInspectionRecordSettings } from '../useInspectionRecordSettings'
import { computeItpExportReadiness } from './qaItpExportReadiness'
import { defaultItpMessage } from './itpDefaultMessage'
import { useQaItpOnedrive } from './useQaItpOnedrive'
import { bytesToBase64 } from './bytesToBase64'
import { useQaItpFrom } from './useQaItpFrom'
import { useQaItpSaveToQDrive } from './useQaItpSaveToQDrive'
import { withSignatureBand } from './itpSignPages'
import { useQaItpSigning } from './useQaItpSigning'

function addressList(raw: string): string[] {
  return raw.split(/[,;\n]/).map((item) => item.trim()).filter(Boolean)
}

export function fileNameFor(name: string, signed: boolean): string {
  if (!signed || /-signed\.pdf$/i.test(name)) return name
  return name.replace(/\.pdf$/i, '-signed.pdf')
}

export function sentNotice(to: string, cc: string, bcc: string, fromLabel: string): string {
  const seen = new Set<string>()
  const people: string[] = []
  for (const item of [...addressList(to), ...addressList(cc), ...addressList(bcc)]) {
    const key = item.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    people.push(item)
  }
  const who = people.length > 1 ? people.join(', ') : to.trim()
  const tail = 'Each person can review, sign and respond from their email.'
  return `Sent to ${who} from ${fromLabel}. ${tail}`
}

function errorMessage(error: unknown): string {
  const err = error as { status?: number; message?: unknown; response?: { status?: number; data?: { error?: string; detail?: string; code?: string; authorize_url?: string } } } | null
  const data = err?.response?.data
  if (data?.error) return data.error
  if (data?.detail) return data.detail
  if ((err?.response?.status ?? err?.status) === 413) return 'The ITP is too large to email. Remove some photos, or use Download PDF and attach it yourself.'
  const status = err?.response?.status ?? err?.status
  if (typeof status === 'number' && status >= 500) return 'The server could not send the email. Press Send again. If it still fails, tell support the time you pressed Send.'
  if (typeof err?.message === 'string' && /timeout/i.test(err.message)) return 'Sending took too long. Check your connection and press Send again.'
  if (error instanceof Error && error.message) return error.message
  return 'Could not send the email.'
}

function coverErrorMessage(err: unknown): string {
  const status = (err as { response?: { status?: number }; status?: number } | null)?.response?.status
    ?? (err as { status?: number } | null)?.status
  if (status === 413) return 'That photo is too large. Choose a smaller photo.'
  return errorMessage(err)
}

export function useQaItpExport({
  api,
  inspectionId,
  jobId,
  orgId,
  executions,
  jobNumber,
  request,
  defaultShowAll,
  reportTitle,
  cover,
  frozenHtml,
}: {
  api: Pick<QaApi, 'renderItpPdf' | 'sendItpEmail' | 'listItpFromMailboxes'> & Partial<Pick<QaApi, 'uploadFile' | 'itpOnedriveStatus' | 'getEvidenceUrls'>>
  inspectionId: string
  jobId: number
  orgId?: number
  executions: QaAreaExecution[]
  jobNumber: string | number
  request: QaItpRequest
  defaultShowAll: boolean
  reportTitle?: string
  cover?: ItpCoverFields
  /** Stored library HTML. When set, Print, Download and Send use this copy. */
  frozenHtml?: string
}) {
  const { client } = useJobHubDataAdapter()
  const queryClient = useQueryClient()
  const canLoadPhotos = typeof api.getEvidenceUrls === 'function'
  const [photoExecutions, setPhotoExecutions] = useState<QaAreaExecution[] | null>(null)
  useEffect(() => {
    if (!canLoadPhotos) {
      setPhotoExecutions(null)
      return
    }
    let cancelled = false
    void areasWithEvidenceUrls(queryClient, api, executions).then((next) => {
      if (!cancelled) setPhotoExecutions(next)
    }).catch(() => {
      if (!cancelled) setPhotoExecutions(executions)
    })
    return () => { cancelled = true }
  }, [api, canLoadPhotos, executions, queryClient])
  const htmlExecutions = canLoadPhotos ? photoExecutions : executions
  const details = useInspectionRecordSettings(inspectionId, jobId, orgId)
  const documentCover = useMemo(() => (
    details.loaded
      ? {
        ...cover,
        clientName: details.clientName,
        clientContactName: details.clientContactName,
        clientAddress: details.clientAddress,
        clientPhone: details.clientPhone,
        clientEmail: details.clientEmail,
        siteAddress: details.siteName,
        coverImageUrl: details.coverImageUrl,
        coverImageWidthMm: details.coverWidthMm,
        coverImageHeightMm: details.coverHeightMm,
        participants: details.participants,
        include: details.include,
      }
      : cover
  ), [
    cover,
    details.loaded,
    details.clientName,
    details.clientContactName,
    details.clientAddress,
    details.clientPhone,
    details.clientEmail,
    details.siteName,
    details.coverImageUrl,
    details.coverWidthMm,
    details.coverHeightMm,
    details.participants,
    details.include,
  ])
  const desktop = useIsMdUp()
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [includeAll, setIncludeAll] = useState(defaultShowAll)
  const [dirty, setDirty] = useState(false)
  const [seedKey, setSeedKey] = useState(0)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const subjectTouched = useRef(false)
  const toTouched = useRef(false)
  const bodyTouched = useRef(false)

  useEffect(() => {
    if (subjectTouched.current) return
    const next = (details.reportTitle || reportTitle || '').trim()
    if (!next) return
    setSubject(next)
    subjectTouched.current = true
  }, [details.reportTitle, reportTitle])

  useEffect(() => {
    if (toTouched.current) return
    const next = details.clientEmail.trim()
    if (!next) return
    setTo(next)
    toTouched.current = true
  }, [details.clientEmail])

  const setSubjectFromUser = (value: string) => {
    subjectTouched.current = true
    setSubject(value)
  }
  const setToFromUser = (value: string) => {
    toTouched.current = true
    setTo(value)
  }
  const setBodyFromUser = (value: string) => {
    bodyTouched.current = true
    setBody(value)
  }

  const liveSeed = useMemo(() => {
    if (!htmlExecutions) return ''
    return buildItpRequestHtml(
      htmlExecutions,
      jobNumber,
      request.areaIds,
      request.checklistIds,
      !includeAll && request.itemIds ? new Set(request.itemIds) : undefined,
      details.reportTitle || reportTitle,
      snapshotQaPhotoViews(),
      documentCover,
    )
  }, [htmlExecutions, jobNumber, request, includeAll, details.reportTitle, reportTitle, documentCover])
  // Reused from: packages/job-hub/src/jobs/qa/fillLibraryPhotoSrc.ts (fillLibraryPhotoSrc)
  // Reused from: packages/job-hub/src/jobs/qa/patchItpCover.ts (patchItpCoverImage)
  const nextSeed = useMemo(() => {
    if (!frozenHtml) return liveSeed
    const filled = fillLibraryPhotoSrc(frozenHtml, photoExecutions ?? executions)
    return details.loaded && details.coverImageUrl
      ? patchItpCoverImage(filled, details.coverImageUrl, { widthMm: details.coverWidthMm, heightMm: details.coverHeightMm })
      : filled
  }, [
    frozenHtml,
    liveSeed,
    photoExecutions,
    executions,
    details.loaded,
    details.coverImageUrl,
    details.coverWidthMm,
    details.coverHeightMm,
  ])
  const [seedHtml, setSeedHtml] = useState(nextSeed)
  const [editedHtml, setEditedHtml] = useState(nextSeed)
  const signing = useQaItpSigning({ api, inspectionId, editedHtml })
  const fromModel = useQaItpFrom(orgId, inspectionId, api.listItpFromMailboxes)

  useEffect(() => {
    if (bodyTouched.current || !fromModel.prefsLoaded) return
    setBody(fromModel.savedMessage || defaultItpMessage(fromModel.userName))
  }, [fromModel.prefsLoaded, fromModel.savedMessage, fromModel.userName])
  const fromLabel = fromModel.from.startsWith('account:')
    ? (fromModel.sendable.find((account) => `account:${account.id}` === fromModel.from)?.email_address || 'your mailbox')
    : fromModel.from === 'login'
      ? `${fromModel.userEmail} via Quotech mail`
      : 'Quotech mail'

  useEffect(() => {
    if (dirty || signing.hasSignatures) return
    setSeedHtml(nextSeed)
    setEditedHtml(nextSeed)
    setSeedKey((current) => current + 1)
  }, [nextSeed, dirty, signing.hasSignatures])

  const hasSelection = (request.itemIds?.length ?? 0) > 0
  const selectedCount = expandItpItemIds(request, executions).length
  const allCount = expandItpItemIds({ ...request, itemIds: null }, executions).length
  const checkCount = expandItpItemIds(
    includeAll ? { ...request, itemIds: null } : request,
    executions,
  ).length
  const readiness = computeItpExportReadiness({
    to,
    subject,
    body,
    clientName: details.clientName,
    reportTitle: details.reportTitle || reportTitle || '',
    checkCount,
    signedStale: signing.handStale,
    hasSigned: signing.hasSignatures,
  })

  const chooseScope = (all: boolean) => {
    setDirty(false)
    setIncludeAll(all)
  }
  const onDocumentChange = (html: string) => {
    setDirty(true)
    setEditedHtml(html)
  }
  const discardEdits = () => {
    setDirty(false)
    setSeedHtml(nextSeed)
    setEditedHtml(nextSeed)
    setSeedKey((current) => current + 1)
  }
  const [livePatch, setLivePatch] = useState<string | null>(null)
  const coverPick = useQaItpExportCover({
    details,
    dirty,
    signed: signing.hasSignatures,
    editedHtml,
    setEditedHtml,
    setLivePatch,
    reportError: (err) => setError(coverErrorMessage(err)),
  })
  const previewRemove = useQaItpPreviewRemove({
    clearCover: () => details.clearCover(),
    patchCover: coverPick.patchCoverInPlace,
    reportError: (err) => setError(coverErrorMessage(err)),
  })
  const coverUploading = () => {
    if (!coverPick.coverBusy) return false
    setNotice('The cover photo is still uploading. Try again in a moment.')
    return true
  }
  const print = () => {
    if (coverUploading()) return
    if (!signing.hasSignatures && signing.items.length === 0) return printItpHtml(editedHtml)
    const popup = needsPrintPopup(desktop) ? window.open('', '_blank') : null
    void signing.resolveSignedBytes()
      .then((bytes) => { if (bytes) printPdfBytes(bytes, popup) })
      .catch((err) => { popup?.close(); setError(errorMessage(err)) })
  }
  const generatedName = defaultItpAttachmentName(jobNumber, details.clientName)
  const [attachmentName, setAttachmentNameState] = useState(generatedName)
  const [nameTouched, setNameTouched] = useState(false)
  useEffect(() => {
    if (!nameTouched) setAttachmentNameState(generatedName)
  }, [generatedName, nameTouched])
  const setAttachmentName = (value: string) => {
    setNameTouched(true)
    setAttachmentNameState(value)
  }
  const drive = useQaItpOnedrive({
    api,
    inspectionId,
    client,
    fromModel,
    setError,
    reportError: (err) => setError(errorMessage(err)),
  })
  const { shareOnedrive, driveAccountId } = drive
  const commitReportTitle = async () => {
    await details.commitReportTitle()
    if (!(dirty || signing.hasSignatures)) return
    const next = patchItpCoverTitle(editedHtml, details.reportTitle)
    setEditedHtml(next)
    setLivePatch(next)
  }
  const signedName = (signed: boolean) => fileNameFor(attachmentName, signed)
  const pdfBlob = async (): Promise<Blob | null> => {
    const signedBytes = await signing.resolveSignedBytes()
    if (signedBytes) {
      const buffer = new ArrayBuffer(signedBytes.byteLength)
      new Uint8Array(buffer).set(signedBytes)
      return new Blob([buffer], { type: 'application/pdf' })
    }
    return null
  }
  const download = async () => {
    if (coverUploading()) return
    const filename = signedName(signing.hasSignatures)
    setDownloading(true)
    setError(null)
    try {
      const handle = await pickPdfSaveLocation(filename)
      const signedBytes = await signing.resolveSignedBytes()
      if (signedBytes) {
        // DOM lib BlobPart rejects Uint8Array<ArrayBufferLike>; copy onto a
        // plain ArrayBuffer so the PDF bytes still round-trip unchanged.
        const buffer = new ArrayBuffer(signedBytes.byteLength)
        new Uint8Array(buffer).set(signedBytes)
        await writePdfToHandleOrDownload(
          new Blob([buffer], { type: 'application/pdf' }),
          filename,
          handle,
        )
      } else {
        await saveItpPdf({ api, inspectionId, html: editedHtml, fileHandle: handle, filename })
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      setError(errorMessage(err))
    } finally {
      setDownloading(false)
    }
  }
  const { savingCopy, saveToQDrive } = useQaItpSaveToQDrive({
    upload: api.uploadFile,
    jobId,
    filename: () => signedName(signing.hasSignatures),
    bytes: async () => (await pdfBlob()) ?? api.renderItpPdf(inspectionId, editedHtml),
    setNotice,
    setError,
    onError: (err) => setError(errorMessage(err)),
  })
  const [sizePrompt, setSizePrompt] = useState<{ sizeMb: number; linkAvailable: boolean } | null>(null)
  const [largePermission, setLargePermission] = useState<{ message: string; authorizeUrl: string } | null>(null)
  const deliver = async (
    mode: 'attach' | 'link' | 'attach_anyway',
    copy?: { kind: 'signed' | 'unsigned'; name: string },
  ) => {
    const wantSigned = !copy || copy.kind === 'signed'
    const editedAfterSign = signing.hasSignatures && signing.handStale
    const signedBytes = !wantSigned || editedAfterSign || signing.inline
      ? null
      : await signing.resolveSignedBytes()
    const usePlacements = wantSigned && (editedAfterSign || signing.inline)
    await api.sendItpEmail(inspectionId, {
      html: wantSigned && signedBytes ? '' : withSignatureBand(editedHtml),
      to: to.trim(),
      cc: cc.trim(),
      bcc: bcc.trim(),
      subject: subject.trim(),
      body,
      ...fromModel.payload,
      attachment_name: copy?.name ?? fileNameFor(attachmentName, Boolean(
        (signing.hasSignatures && !editedAfterSign && signedBytes) || editedAfterSign,
      )),
      share_onedrive: shareOnedrive,
      ...(shareOnedrive && driveAccountId != null ? { onedrive_account_id: driveAccountId } : {}),
      attachment_mode: mode,
      ...(copy ? { copy_kind: copy.kind } : {}),
      ...(signedBytes ? { signed_pdf_base64: bytesToBase64(signedBytes) } : {}),
      ...(usePlacements ? { placements: signing.placements } : {}),
    })
    setSizePrompt(null)
    setLargePermission(null)
    setNotice(sentNotice(to, cc, bcc, fromLabel))
  }
  const send = async (
    mode: 'attach' | 'link' | 'attach_anyway' = 'attach',
    copy?: { kind: 'signed' | 'unsigned'; name: string },
  ) => {
    if (coverUploading() || readiness.blockers.length > 0 || sending) return
    if (mode === 'attach' && !copy && !nameTouched && !window.confirm(`The PDF will be sent as "${attachmentName}". Send with this name?`)) return
    setSending(true)
    setError(null)
    setNotice(null)
    try {
      await flushPendingQuoteDetails()
      await deliver(mode, copy)
    } catch (err) {
      const data = (err as { response?: { data?: { code?: string; error?: string; size_mb?: number; link_available?: boolean; authorize_url?: string } } })?.response?.data
      if (data?.code === 'onedrive_consent_required') {
        await drive.onConsentRequired()
      } else if (data?.code === 'pdf_over_limit') {
        setSizePrompt({ sizeMb: Number(data.size_mb ?? 0), linkAvailable: data.link_available === true })
      } else if (data?.code === 'large_send_failed') {
        // Outlook stopped part way through a large attachment. The link
        // dialog is the way out, and the message names the step that failed.
        setSizePrompt({ sizeMb: Number(data.size_mb ?? 0), linkAvailable: data.link_available === true })
        setError(data.error || 'Outlook could not send the email. Send it as a OneDrive link instead.')
      } else if (data?.code === 'mailbox_needs_large_permission') {
        setLargePermission({
          message: data.error || 'Allow larger attachments, then send again.',
          authorizeUrl: data.authorize_url || '',
        })
        setError(data.error || 'Allow larger attachments, then send again.')
      } else {
        setError(errorMessage(err))
      }
    } finally {
      setSending(false)
    }
  }

  return {
    details: { ...details, commitReportTitle }, to, setTo: setToFromUser, cc, setCc, bcc, setBcc, subject, setSubject: setSubjectFromUser, body, setBody: setBodyFromUser, fromLabel,
    seedHtml, seedKey, editedHtml, livePatch, onDocumentChange, dirty, discardEdits,
    coverPreviewUrl: details.coverImageUrl,
    chooseCover: coverPick.chooseCover,
    coverBusy: coverPick.coverBusy,
    removeSavedCover: previewRemove.removeSavedCover,
    coverWidthMm: details.coverWidthMm,
    coverHeightMm: details.coverHeightMm,
    setCoverWidthMm: coverPick.setCoverWidthMm,
    setCoverHeightMm: coverPick.setCoverHeightMm,
    commitCoverSize: details.commitCoverSize,
    patchIncludeInPlace: coverPick.patchIncludeInPlace,
    includeAll, chooseScope, hasSelection, selectedCount, allCount, checkCount,
    signing,
    fromModel,
    attachmentName, setAttachmentName, shareOnedrive, setShareOnedrive: drive.setShareOnedrive, onedriveAllowed: true,
    onedriveApproval: drive.approval,
    copyAdminLink: drive.copyLink,
    signInOnedrive: drive.signIn,
    useOnedriveAttachment: drive.useAttachment,
    print, download, saveToQDrive: () => (coverUploading() ? Promise.resolve() : saveToQDrive()), send, readiness, notice, error, setNotice, setError, sending, downloading, savingCopy,
    sizePrompt, dismissSize: () => setSizePrompt(null), largePermission,
  }
}
